/* Assinatura eletrônica simples da procuração.

   Base legal: Lei 14.063/2020, art. 4º, I. A assinatura eletrônica simples
   identifica o signatário e anexa dados capazes de verificar a integridade
   do documento. Vale entre as partes quando não há exigência de forma mais
   rigorosa. Não é a mesma coisa que assinatura qualificada com certificado
   ICP-Brasil: para o INSS, o caminho robusto continua sendo o Gov.br, e o
   PDF gerado aqui pode ser levado a assinador.iti.br a qualquer momento.

   O que capturamos para dar lastro à assinatura:
   - o traço do dedo na tela
   - data e hora do aparelho
   - o e-mail de quem colheu (o captador logado)
   - um código derivado do conteúdo do documento

   Quem não sabe ou não pode assinar: a lei civil admite assinatura a rogo,
   com duas testemunhas identificadas. Está previsto no modo "rogo".

   IMPORTANTE: biometria de celular não serve aqui. O leitor de digital
   autentica o DONO do aparelho, não captura a digital de um terceiro.
   Usar isso como "assinatura do cliente" seria falso. */

import { $, esc, toast } from "../util.js";

/* Desenha na tela e devolve o traço em PNG.
   Suporta dedo, caneta e mouse pelo mesmo caminho (Pointer Events). */
function montarCanvas(cv){
  const ctx = cv.getContext("2d");
  const escala = window.devicePixelRatio || 1;
  const r = cv.getBoundingClientRect();
  cv.width  = Math.round(r.width  * escala);
  cv.height = Math.round(r.height * escala);
  ctx.scale(escala, escala);
  ctx.lineWidth = 2.4;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = "#101820";

  let desenhando = false, ultimo = null, temTraco = false;

  const ponto = e => {
    const b = cv.getBoundingClientRect();
    return { x: e.clientX - b.left, y: e.clientY - b.top };
  };
  const comecar = e => {
    e.preventDefault();
    desenhando = true; temTraco = true;
    ultimo = ponto(e);
    cv.setPointerCapture?.(e.pointerId);
  };
  const mover = e => {
    if (!desenhando) return;
    e.preventDefault();
    const p = ponto(e);
    ctx.beginPath();
    ctx.moveTo(ultimo.x, ultimo.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    ultimo = p;
  };
  const parar = () => { desenhando = false; };

  cv.addEventListener("pointerdown", comecar);
  cv.addEventListener("pointermove", mover);
  cv.addEventListener("pointerup", parar);
  cv.addEventListener("pointercancel", parar);
  cv.addEventListener("pointerleave", parar);

  return {
    limpar(){ ctx.clearRect(0, 0, cv.width, cv.height); temTraco = false; },
    vazio(){ return !temTraco; },
    /* Fundo branco explícito: PNG transparente some no PDF. */
    png(){
      const saida = document.createElement("canvas");
      saida.width = cv.width; saida.height = cv.height;
      const c2 = saida.getContext("2d");
      c2.fillStyle = "#FFFFFF";
      c2.fillRect(0, 0, saida.width, saida.height);
      c2.drawImage(cv, 0, 0);
      return saida.toDataURL("image/png");
    }
  };
}

/* Código de verificação derivado do conteúdo. Muda se qualquer dado mudar,
   o que permite conferir depois se o documento é o mesmo que foi assinado. */
export async function codigoAssinatura(caso, quando, porQuem){
  const base = [
    caso.id, caso.cliente, caso.cpf, caso.beneficio,
    quando, porQuem
  ].join("|").toUpperCase();

  try {
    const bytes = new TextEncoder().encode(base);
    const hash = await crypto.subtle.digest("SHA-256", bytes);
    const hex = [...new Uint8Array(hash)].map(b => b.toString(16).padStart(2, "0")).join("");
    return "AS-" + hex.slice(0, 10).toUpperCase();
  } catch {
    let h = 0;
    for (let i = 0; i < base.length; i++) h = (h * 31 + base.charCodeAt(i)) >>> 0;
    return "AS-" + h.toString(36).toUpperCase().padStart(10, "0").slice(0, 10);
  }
}

/* Abre a tela de assinatura. Resolve com os dados da assinatura, ou null se cancelar. */
export function abrirAssinatura(caso, emailColheu){
  return new Promise(resolve => {
    const el = document.createElement("div");
    el.className = "assina";
    el.innerHTML = `
      <div class="assina-cx">
        <div class="assina-topo">
          <div>
            <h2>Assinatura da procuração</h2>
            <p>${esc(caso.cliente || "")}</p>
          </div>
          <button class="x" data-x aria-label="Fechar">&times;</button>
        </div>

        <div class="assina-modo" id="assinaModo">
          <button type="button" data-modo="tela" aria-pressed="true">O cliente assina</button>
          <button type="button" data-modo="rogo" aria-pressed="false">Assina a rogo</button>
        </div>

        <div id="blocoTela">
          <p class="assina-dica">Peça para o cliente assinar com o dedo dentro da moldura. Vire o celular na horizontal se ficar apertado.</p>
          <div class="assina-quadro">
            <canvas id="assinaCanvas"></canvas>
            <span class="assina-linha"></span>
          </div>
        </div>

        <div id="blocoRogo" hidden>
          <p class="assina-dica">Quando o cliente não sabe ou não pode assinar, outra pessoa assina a rogo, na presença de duas testemunhas identificadas. Os três nomes entram na procuração.</p>
          <div class="field"><label for="aRogo">Quem assina a rogo</label>
            <input id="aRogo" type="text" placeholder="Nome completo e CPF"></div>
          <div class="field"><label for="aT1">Testemunha 1</label>
            <input id="aT1" type="text" placeholder="Nome completo e CPF"></div>
          <div class="field"><label for="aT2">Testemunha 2</label>
            <input id="aT2" type="text" placeholder="Nome completo e CPF"></div>
          <div class="assina-quadro" style="margin-top:12px">
            <canvas id="assinaCanvas2"></canvas>
            <span class="assina-linha"></span>
          </div>
          <p class="assina-dica">Assinatura de quem assina a rogo.</p>
        </div>

        <p class="assina-aviso">
          Ao confirmar, o sistema registra data, hora, o e-mail de quem colheu e um código
          de verificação do documento. Isso vale como assinatura eletrônica simples
          (Lei 14.063/2020). Para o padrão do Gov.br, use também o assinador oficial.
        </p>

        <div class="assina-pe">
          <button class="btn ghost" data-limpar>Limpar</button>
          <div class="spacer"></div>
          <button class="btn ghost" data-x>Cancelar</button>
          <button class="btn" data-ok>Confirmar assinatura</button>
        </div>
      </div>`;

    document.body.appendChild(el);

    const painel  = montarCanvas(el.querySelector("#assinaCanvas"));
    const painel2 = montarCanvas(el.querySelector("#assinaCanvas2"));
    let modo = "tela";

    el.querySelector("#assinaModo").addEventListener("click", e => {
      const b = e.target.closest("[data-modo]"); if (!b) return;
      modo = b.dataset.modo;
      el.querySelectorAll("[data-modo]").forEach(x => x.setAttribute("aria-pressed", x === b));
      el.querySelector("#blocoTela").hidden = modo !== "tela";
      el.querySelector("#blocoRogo").hidden = modo !== "rogo";
    });

    const fechar = () => { el.remove(); resolve(null); };
    el.querySelectorAll("[data-x]").forEach(b => b.onclick = fechar);
    el.querySelector("[data-limpar]").onclick = () => {
      (modo === "tela" ? painel : painel2).limpar();
    };

    el.querySelector("[data-ok]").onclick = async () => {
      const p = modo === "tela" ? painel : painel2;
      if (p.vazio()){ toast("Falta a assinatura na moldura."); return; }

      const dados = { modo, imagem: p.png(), quando: new Date().toISOString(), colheu: emailColheu };

      if (modo === "rogo"){
        const rogo = el.querySelector("#aRogo").value.trim();
        const t1   = el.querySelector("#aT1").value.trim();
        const t2   = el.querySelector("#aT2").value.trim();
        if (!rogo || !t1 || !t2){
          toast("Informe quem assina a rogo e as duas testemunhas.");
          return;
        }
        Object.assign(dados, { rogo, testemunhas: [t1, t2] });
      }

      dados.codigo = await codigoAssinatura(caso, dados.quando, emailColheu);
      el.remove();
      resolve(dados);
    };
  });
}
